import { useMemo } from "react";

import {
  ArrowTopRightOnSquareIcon,
  ClockIcon,
  ShoppingBagIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";
import { DateTime } from "luxon";

import PartGradeBadge from "../../shared/PartGradeBadge";
import StockStatusEditor from "./StockStatusEditor";
import classNames from "../../../utils/classNames";
import formatDateTime from "../../../utils/formatDateTime";
import openInNewTab from "../../../utils/openInNewTab";
import PriceFormat from "../../../utils/priceFormat";
import { IPart, PartStatus } from "../../../models/Part";

const daysSince = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const dt = DateTime.fromISO(iso);
  if (!dt.isValid) return null;
  const diff = Math.floor(DateTime.now().diff(dt, "days").days);
  return diff < 0 ? 0 : diff;
};

interface StockImageSummaryPanelProps {
  part: IPart;
  /** Rendered on a dark background (inside the image lightbox). */
  onDark?: boolean;
}

/**
 * Compact, read-only summary of a part — mirrors the fields shown in
 * the Stock detail page `StockHeader`, re-skinned for use inside the
 * image lightbox so users get the key context (title, vehicle, VRM,
 * grade, status, price, time-in-stock) without leaving the modal.
 *
 * A prominent "View full stock page" button on the panel jumps to
 * `/stock/:partId` for the full detail view when the user wants more.
 */
export default function StockImageSummaryPanel({
  part,
  onDark,
}: StockImageSummaryPanelProps) {
  const vehicleLabel = useMemo(() => {
    const year = part.vehicle?.dvlaYearOfManufacture ?? null;
    const make = part.vehicle?.combinedMake ?? null;
    const model = part.vehicle?.combinedModel ?? null;
    return [year, make, model].filter(Boolean).join(" ");
  }, [part.vehicle]);

  const stockStartIso = part.removedOn ?? part.created ?? null;
  const daysInStock = useMemo(() => daysSince(stockStartIso), [stockStartIso]);

  const firstOffer = part.offers?.[0];
  const listedIso = part.listedDate ?? firstOffer?.listingStarted ?? null;
  const daysListed = useMemo(() => daysSince(listedIso), [listedIso]);

  const isSold = part.partStatus === PartStatus.Sold || !!part.price;
  const soldPrice = isSold ? part.price ?? null : null;
  const listPrice = part.enteredPrice ?? null;

  const hasViewButton = !!part.id;

  const wrapperClasses = classNames(
    "flex h-full flex-col gap-4 rounded-lg p-4 ring-1 ring-inset",
    onDark
      ? "bg-white/95 text-slate-800 ring-white/20 shadow-lg"
      : "bg-white text-slate-800 ring-slate-200"
  );

  return (
    <div className={wrapperClasses}>
      <div className="min-w-0">
        <h3 className="text-base font-semibold leading-snug text-slate-900 line-clamp-3">
          {part.title ?? "Untitled Part"}
        </h3>
        {vehicleLabel && (
          <p className="mt-1 text-sm text-slate-600">{vehicleLabel}</p>
        )}
        {part.category?.name && (
          <p className="mt-0.5 text-xs text-slate-500">
            {part.category.name}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <StockStatusEditor part={part} />
        <PartGradeBadge part={part} />
      </div>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
        {part.tagNumber && (
          <Row label="Tag" value={part.tagNumber} />
        )}
        {part.vehicle?.vrm && (
          <Row label="VRM" value={part.vehicle.vrm} />
        )}
        {part.partNumber && (
          <Row label="Part #" value={part.partNumber} />
        )}
        {part.ebayItemNumber && (
          <Row label="eBay item" value={part.ebayItemNumber} />
        )}
      </dl>

      <div className="rounded-md bg-slate-50 p-3 ring-1 ring-inset ring-slate-200">
        {isSold ? (
          <>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Sold
            </p>
            <p className="mt-0.5 text-2xl font-semibold text-slate-900">
              {PriceFormat(soldPrice ?? 0)}
            </p>
            {part.soldDate && (
              <p className="mt-0.5 text-[11px] text-slate-500">
                on{" "}
                {formatDateTime(part.soldDate, DateTime.DATE_MED, "")}
              </p>
            )}
          </>
        ) : (
          <>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Listed price
            </p>
            <p className="mt-0.5 text-2xl font-semibold text-slate-900">
              {listPrice !== null ? PriceFormat(listPrice) : "—"}
            </p>
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        {daysInStock !== null && (
          <Badge
            icon={<ClockIcon className="h-3.5 w-3.5" aria-hidden />}
            tone="slate"
            title={`In stock since ${formatDateTime(
              stockStartIso,
              DateTime.DATE_MED,
              "-"
            )}`}
          >
            {daysInStock} {daysInStock === 1 ? "day" : "days"} in stock
          </Badge>
        )}
        {daysListed !== null && (
          <Badge
            icon={<ShoppingBagIcon className="h-3.5 w-3.5" aria-hidden />}
            tone="blue"
            title={`Listed on ${formatDateTime(
              listedIso,
              DateTime.DATE_MED,
              "-"
            )}`}
          >
            {daysListed} {daysListed === 1 ? "day" : "days"} on eBay
          </Badge>
        )}
      </div>

      <div className="mt-auto flex flex-col gap-2">
        {hasViewButton && (
          <button
            type="button"
            onClick={() => openInNewTab(`/stock/${part.id}`)}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden />
            View full stock page
          </button>
        )}
        {part.vehicle?.id && (
          <button
            type="button"
            onClick={() => openInNewTab(`/vehicles/${part.vehicle?.id}`)}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <TruckIcon className="h-4 w-4" aria-hidden />
            View vehicle stock
          </button>
        )}
        {part.ebayItemNumber && (
          <a
            href={`https://www.ebay.co.uk/itm/${part.ebayItemNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden />
            View on eBay
          </a>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </dt>
      <dd className="truncate text-slate-700" title={value}>
        {value}
      </dd>
    </div>
  );
}

const BADGE_TONES: Record<string, string> = {
  slate: "bg-slate-100 text-slate-700 ring-slate-200",
  blue: "bg-blue-50 text-blue-700 ring-blue-200",
};

function Badge({
  children,
  icon,
  tone = "slate",
  title,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  tone?: keyof typeof BADGE_TONES;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={classNames(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ring-1 ring-inset",
        BADGE_TONES[tone] ?? BADGE_TONES.slate
      )}
    >
      {icon}
      {children}
    </span>
  );
}
